import React from "react";
import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import LessonView from "@/pages/LessonView";
import AdminPanel from "@/pages/AdminPanel";
import Certificate from "@/pages/Certificate";
import OutlookPage from "@/pages/OutlookPage";
import IntelligencePage from "@/pages/IntelligencePage";
import IntroPage from "@/pages/IntroPage";
import SetPassword from "@/pages/SetPassword";
import Verify from "@/pages/Verify";

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#050505] text-[#D4AF37] font-display text-2xl tracking-widest">
      ◇ THE BOARDROOM ◇
    </div>
  );
}

function Protected({ children, adminOnly = false }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === undefined) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password && loc.pathname !== "/set-password") {
    return <Navigate to="/set-password" replace />;
  }
  // Students must acknowledge the Introduction to Trading before curriculum unlocks.
  // Admins are exempt.
  if (user.role === "student" && !user.intro_acknowledged_at && loc.pathname !== "/intro") {
    return <Navigate to="/intro" replace />;
  }
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/verify/:code" element={<Verify />} />
      <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <Login />} />
      <Route path="/set-password" element={
        user === undefined ? <Loading /> :
        user ? <SetPassword forced={!!user.must_change_password} /> :
        <Navigate to="/login" replace />
      } />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/intro" element={<Protected><IntroPage /></Protected>} />
      <Route path="/module/:moduleId" element={<Protected><LessonView /></Protected>} />
      <Route path="/certificate" element={<Protected><Certificate /></Protected>} />
      <Route path="/outlook" element={<Protected><OutlookPage /></Protected>} />
      <Route path="/intel" element={<Protected><IntelligencePage /></Protected>} />
      <Route path="/admin" element={<Protected adminOnly><AdminPanel /></Protected>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
